import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import numeral from 'numeral';
import moment from 'moment';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';

interface TransportCostsFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    documents: any[];
    costs: any;
    schema: any;
    delivery_instances_schema: any;
    delivery_date: any;

    meta: any;
}

const state: TransportCostsFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    documents: [],
    costs: {},
    schema: null,
    delivery_instances_schema: null,
    delivery_date: null,

    meta: {},
};

const initState: TransportCostsFormState = cloneDeep(state);

export const TRANSPORT_COSTS_DATA = 'GET_TRANSPORT_COSTS_DATA';
export const TRANSPORT_COSTS_SCHEMA = 'GET_TRANSPORT_COSTS_SCHEMA';
export const DELIVERY_INSTANCES_SCHEMA = 'GET_DELIVERY_INSTANCES_SCHEMA';

export const actions: ActionTree<TransportCostsFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadDeliveryInstanes({ commit }, {ids}) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const queries = [];
            if (ids.length > 0) {
                queries.push(
                    vm.$q(TRANSPORT_COSTS_DATA)
                    .raw('documents.list')
                    .action({
                        "columns": {
                            id: 'di.id',
                            name: 'dz.name',
                            cost: 'di.cost',
                            created_at: 'di.created_at',
                            delivery_date: 'di.delivery_date',
                            driver_fixed_rate: 'di.driver_fixed_rate',
                            driver_hour_rate: 'di.driver_hour_rate',
                            loading_time: 'di.loading_time',
                            updated_at: 'di.updated_at',
                            weight: "dw.weight",
                        },
                        from: 's:delivery_instances',
                        from_as: 'di',
                        joins: [
                            {
                                join: 's:delivery_zones',
                                type: 'left',
                                as:   'dz',
                                on: {
                                    $eq: ['dz.id', 'di.delivery_zone_id']
                                }
                            },
                            {
                                join: {
                                    columns: {
                                        id: "di.id",
                                        weight: { $sum: 'vdt.total_weight' },
                                    }, 
                                    from: "s:delivery_instances",
                                    from_as: "di",
                                    joins: [
                                        {
                                            join: "s:delivery_instance_deals",
                                            as: "did",
                                            on: {
                                                $eq: ["di.id", "did.delivery_instance_id"]
                                            }
                                        },
                                        {
                                            join: "s:view_deals_totals",
                                            as: "vdt",
                                            on: {
                                                $eq: ["vdt.id", "did.deal_id"]
                                            }
                                        },
                                    ], 
                                    groups: [
                                        "di.id"
                                    ]
                                },
                                type: 'left',
                                as:   'dw',
                                on: {
                                    $eq: ['di.id', 'dw.id']
                                }
                            }
                        ],
                        where: {
                            $in: ['di.id', {$: ids}]
                        }
                    }))
            }
            vm.$db([
                ...queries,
                vm.$q(TRANSPORT_COSTS_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:transport_costs`
                    }),
                vm.$q(DELIVERY_INSTANCES_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:delivery_instances`
                    }),
            ]).then((result: ServerResult) => {
                if (ids.length > 0) {
                    commit('SET_FORM_DATA', result.getOperation(TRANSPORT_COSTS_DATA).getContent());
                }
                commit('SET_SCHEMA', result.getOperation(TRANSPORT_COSTS_SCHEMA).getContent());
                commit('SET_DELIVERY_INSTANCES_SCHEMA', result.getOperation(TRANSPORT_COSTS_SCHEMA).getContent());

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit, state }, documentId) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const saveQueries = state.documents.map((item: any, idx: number) => {
                return  vm.$q('DELIVERY_INTSANCE_SAVE-' + idx)
                    .update(item.id, {
                        id: item.id,
                        cost: numeral(item.cost).value().toFixed(2)
                    })
            })

            Object.values(state.costs).map((item: any, idx: number) => {
                saveQueries.push(vm.$q('TRANSPORT_COSTS_SAVE-' + idx)
                .create('s:transport_costs', item))
            })

            vm.$db(saveQueries).then((result: ServerResult) => {
                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {idx, field, newValue}) {
        commit('CHANGE_VALUE', {idx, field, newValue});
    },

    setTransportCostValue({commit}, {purpose, newValue, delivery_zone_group_id}) {
        commit('SET_TRANSPORT_COST_VALUE', {purpose, newValue, delivery_zone_group_id});
    },
    setDeliveryDate({commit}, {delivery_date}) {
        commit('SET_DELIVRY_DATE', {delivery_date});
    },
};

export const mutations: MutationTree<TransportCostsFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {documents}) {
        state.documents = documents.map((item: any) => {
            item.cost = parseFloat(item.cost) || 0
            return item
        });
        state.costs = {}
    },
    SET_SCHEMA(state, {schema}) {
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
    },

    SET_DELIVERY_INSTANCES_SCHEMA(state, {schema}) {
        state.schema = schema;
    },

    SET_DELIVRY_DATE(state, {delivery_date}) {
        state.delivery_date = delivery_date;
    },

    SET_TRANSPORT_COST_VALUE(state, {purpose, newValue, delivery_zone_group_id}) {
        state.costs[purpose] = {
            purpose: purpose, 
            costs: numeral(newValue).value().toFixed(2),
            date: state.delivery_date,
            delivery_zone_group_id: 'd:delivery_zone_groups:' + delivery_zone_group_id
        }
    },

    CHANGE_VALUE(state: TransportCostsFormState, {idx, field, newValue}) {
        switch (field) {
            default:
                state.documents[idx][field] = newValue;
        }
    },
};

const getters = {
    fields: (state: TransportCostsFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_transport_costs_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
